/**
 * SAAgencyList — Expandable agency cards for Superadmin cockpit
 * Pattern: ExpandableKPI (framer-motion AnimatePresence)
 */
import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { AnimatePresence, motion } from 'framer-motion';
import { colors } from '../../styles/colors';

const getScoreColor = (score) => {
  if (score >= 80) return colors.success;
  if (score >= 65) return colors.primary;
  if (score >= 50) return colors.warning;
  return colors.danger;
};

const tierStyles = {
  premium: { bgcolor: colors.infoBg, color: colors.info },
  pro: { bgcolor: colors.primaryBg, color: colors.primary },
  essentiel: { bgcolor: colors.neutralBg, color: colors.textMed },
};

const paymentStyles = {
  paid: { bgcolor: colors.successBg, color: colors.successDark, label: 'Paye' },
  due: { bgcolor: colors.warningBg, color: colors.warningDark, label: 'A payer' },
  overdue: { bgcolor: colors.dangerBg, color: colors.dangerDark, label: 'En retard' },
};

function EvolutionChip({ delta }) {
  if (delta == null || delta === 0) {
    return (
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          bgcolor: colors.warningBg, color: colors.warning,
          borderRadius: '100px', px: 1, py: 0.25,
          fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
        }}
      >
        <TrendingFlatIcon sx={{ fontSize: 14 }} />
        =
      </Box>
    );
  }
  const isPositive = delta > 0;
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        bgcolor: isPositive ? colors.successBg : colors.dangerBg,
        color: isPositive ? colors.success : colors.danger,
        borderRadius: '100px', px: 1, py: 0.25,
        fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
      }}
    >
      {isPositive ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
      {isPositive ? `+${delta.toFixed(1)}` : delta.toFixed(1)}
    </Box>
  );
}

function AgencyCard({ agency }) {
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const payStyle = paymentStyles[agency.payment_status] || paymentStyles.due;

  const glowGradient = isHovered
    ? `radial-gradient(250px circle at ${mousePos.x}px ${mousePos.y}px, rgba(27,94,64,0.07), transparent 60%)`
    : 'none';

  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        borderRadius: '20px',
        border: `1px solid ${colors.borderLight}`,
        background: colors.cardGlass,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        p: '26px 28px',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          transform: 'translateY(-2px)',
          borderColor: colors.primaryLight,
        },
      }}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Mouse glow */}
      <Box
        sx={{
          position: 'absolute', inset: 0,
          background: glowGradient,
          pointerEvents: 'none', zIndex: 0, borderRadius: 'inherit',
        }}
      />

      {/* Collapsed content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 200px' }}>
            <Box
              sx={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                bgcolor: agency.accent_color || colors.primary,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem',
                fontWeight: 600, color: colors.charcoal,
              }}
            >
              {agency.name}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem',
                fontWeight: 500, color: colors.charcoal,
              }}
            >
              {(agency.mrr || 0).toLocaleString('fr-FR')} EUR
            </Typography>

            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem',
                color: colors.textMed,
              }}
            >
              {agency.brands_count} / {agency.brands_max} marques
            </Typography>

            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif', fontSize: '0.95rem',
                fontWeight: 500, color: getScoreColor(agency.score_avg || 0),
              }}
            >
              {(agency.score_avg || 0).toFixed(1)}
            </Typography>

            {agency.crises_active > 0 && (
              <Chip
                label={`${agency.crises_active} crise${agency.crises_active > 1 ? 's' : ''}`}
                size="small"
                sx={{
                  bgcolor: colors.dangerBg, color: colors.danger,
                  height: 22, fontSize: '0.68rem', fontWeight: 600, borderRadius: '100px',
                  fontFamily: '"DM Sans", sans-serif',
                }}
              />
            )}

            <Chip
              label={payStyle.label}
              size="small"
              sx={{
                bgcolor: payStyle.bgcolor, color: payStyle.color,
                height: 22, fontSize: '0.68rem', fontWeight: 600, borderRadius: '100px',
                fontFamily: '"DM Sans", sans-serif',
              }}
            />

            <ExpandMoreIcon
              sx={{
                fontSize: 20, color: colors.textMed,
                transition: 'transform 0.3s ease',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>
        </Box>

        {/* Expanded content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}` }}>
                {agency.brands && agency.brands.length > 0 ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Marque', 'Score', 'Evolution', 'Forfait', 'Crises', 'Actions'].map((col) => (
                            <th
                              key={col}
                              style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.72rem',
                                color: colors.textMed,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                padding: '12px',
                                borderBottom: `1px solid ${colors.borderLight}`,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {agency.brands.map((brand, idx) => {
                          const tier = brand.tier || 'essentiel';
                          const tStyle = tierStyles[tier] || tierStyles.essentiel;
                          const sColor = getScoreColor(brand.score_global || 0);
                          return (
                            <tr key={brand.id}>
                              <td
                                style={{
                                  padding: '14px 12px', fontSize: '0.88rem', color: colors.text,
                                  borderBottom: idx === agency.brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                                  verticalAlign: 'middle',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sColor, flexShrink: 0 }} />
                                  <Typography
                                    sx={{
                                      fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                                      color: colors.charcoal, fontSize: '0.8rem',
                                    }}
                                  >
                                    {brand.name}
                                  </Typography>
                                </Box>
                              </td>
                              <td
                                style={{
                                  padding: '14px 12px',
                                  borderBottom: idx === agency.brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                                  verticalAlign: 'middle',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: '0.95rem', fontWeight: 500, color: sColor,
                                  }}
                                >
                                  {brand.score_global != null ? brand.score_global.toFixed(1) : '—'}
                                </Typography>
                              </td>
                              <td
                                style={{
                                  padding: '14px 12px',
                                  borderBottom: idx === agency.brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                                  verticalAlign: 'middle',
                                }}
                              >
                                <EvolutionChip delta={brand.score_delta} />
                              </td>
                              <td
                                style={{
                                  padding: '14px 12px',
                                  borderBottom: idx === agency.brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                                  verticalAlign: 'middle',
                                }}
                              >
                                <Chip
                                  label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                                  size="small"
                                  sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.68rem', fontWeight: 600,
                                    color: tStyle.color, bgcolor: tStyle.bgcolor,
                                    borderRadius: '100px', height: 22,
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  padding: '14px 12px',
                                  borderBottom: idx === agency.brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                                  verticalAlign: 'middle',
                                }}
                              >
                                {brand.crises_active > 0 ? (
                                  <Chip
                                    label={brand.crises_active}
                                    size="small"
                                    sx={{
                                      bgcolor: colors.dangerBg, color: colors.danger,
                                      height: 22, fontSize: '0.68rem', fontWeight: 600,
                                      borderRadius: '100px', fontFamily: "'DM Sans', sans-serif",
                                    }}
                                  />
                                ) : (
                                  <Typography sx={{ color: colors.textLight, fontSize: '0.8rem' }}>—</Typography>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: '14px 12px',
                                  borderBottom: idx === agency.brands.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                                  verticalAlign: 'middle',
                                }}
                              >
                                {brand.actions_pending > 0 ? (
                                  <Chip
                                    label={brand.actions_pending}
                                    size="small"
                                    sx={{
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontSize: '0.72rem', fontWeight: 600,
                                      bgcolor: colors.primaryBg, color: colors.primary,
                                      height: 22, minWidth: 28, borderRadius: '100px',
                                    }}
                                  />
                                ) : (
                                  <Typography sx={{ color: colors.textLight, fontSize: '0.8rem' }}>--</Typography>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textLight }}>
                    Aucune marque
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Box
                    component="button"
                    onClick={(e) => { e.stopPropagation(); }}
                    sx={{
                      fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', fontWeight: 600,
                      color: colors.primary, border: `1px solid ${colors.primaryLight}`,
                      bgcolor: 'transparent', borderRadius: '100px', px: 2, py: 0.75,
                      cursor: 'pointer', textTransform: 'none',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: colors.primaryBg },
                    }}
                  >
                    Voir facturation
                  </Box>
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}

function SAAgencyList({ agencies }) {
  if (!agencies || agencies.length === 0) {
    return (
      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textLight, py: 3, textAlign: 'center' }}>
        Aucune agence
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {agencies.map((agency) => (
        <AgencyCard key={agency.id} agency={agency} />
      ))}
    </Box>
  );
}

export default SAAgencyList;
